import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RankingBarChart from '../charts/RankingBarChart';
import { useApiQuery } from '../../hooks/useApiCache';

const getRealizadoColor = (valor) => {
  if (valor >= 100) return 'success';
  if (valor >= 80) return 'warning';
  return 'error';
};

const getRankIcon = (position) => {
  if (position === 1) return '🥇';
  if (position === 2) return '🥈';
  if (position === 3) return '🥉';
  return `${position}º`;
};

const TurnosRankingModal = ({ open, onClose, startDate, endDate, contrato, centro, equipe }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headerBg = isDark ? '#153444' : 'grey.100';
  const rowBgEven = isDark ? 'rgba(255, 255, 255, 0.04)' : 'background.default';
  const rowBgOdd = isDark ? 'rgba(255, 255, 255, 0.08)' : 'grey.50';
  const boxBg = isDark ? 'rgba(255, 255, 255, 0.08)' : 'grey.50';

  // Estado para controlar ordenação (desc = maior para menor, asc = menor para maior)
  const [sortOrder, setSortOrder] = useState('desc');

  // Função para alternar ordenação
  const handleToggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Buscar dados do ranking com cache (equipes)
  const { data: rankingData, isLoading: loading, error: queryError } = useApiQuery(
    '/percentual-turnos-diario-ranking',
    { startDate, endDate, contrato, centro, equipe },
    { 
      enabled: open && !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000 // 5 minutos de cache
    }
  );

  // Buscar dados por contrato para o gráfico
  const { data: contratosData } = useApiQuery(
    '/percentual-turnos-contrato',
    { startDate, endDate, contrato, centro, equipe },
    {
      enabled: open && !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000
    }
  );

  const error = queryError?.message || null;

  // Converter filtro de equipes para array para validação no frontend
  const equipesPermitidas = React.useMemo(() => {
    if (!equipe || equipe === 'Todos') return null; // null = sem filtro, exibir todas
    if (Array.isArray(equipe)) return equipe;
    if (typeof equipe === 'string') return equipe.split(',').map(e => e.trim());
    return null;
  }, [equipe]);

  // Função para verificar se equipe está na lista de filtro
  const isEquipePermitida = (nomeEquipe) => {
    if (!equipesPermitidas) return true; // Sem filtro, exibir todas
    return equipesPermitidas.includes(nomeEquipe);
  };

  // Processar dados do ranking
  const { top10, bottom10, allData } = React.useMemo(() => {
    if (!rankingData || !rankingData.melhores || !rankingData.piores) {
      return { top10: [], bottom10: [], allData: [] };
    }

    // Combinar melhores e piores, remover duplicatas e ordenar
    const todasEquipesMap = new Map();
    
    // Adicionar melhores ao map
    (rankingData.melhores || [])
      .filter(item => isEquipePermitida(item.des_equipe))
      .forEach(item => {
        todasEquipesMap.set(item.des_equipe, {
          des_equipe_cms: item.des_equipe,
          total_previsto: item.total_previsto || 0,
          total_realizado: item.total_aberto || 0,
          percentual_realizado: item.percentual_realizado || 0
        });
      });
    
    // Adicionar piores ao map (ignora se já existe)
    (rankingData.piores || [])
      .filter(item => isEquipePermitida(item.des_equipe))
      .forEach(item => {
        if (!todasEquipesMap.has(item.des_equipe)) {
          todasEquipesMap.set(item.des_equipe, {
            des_equipe_cms: item.des_equipe,
            total_previsto: item.total_previsto || 0,
            total_realizado: item.total_aberto || 0,
            percentual_realizado: item.percentual_realizado || 0
          });
        }
      });

    // Converter para array e ordenar por percentual
    const todasEquipes = Array.from(todasEquipesMap.values());
    const ordenadas = [...todasEquipes].sort((a, b) => 
      sortOrder === 'desc' 
        ? b.percentual_realizado - a.percentual_realizado
        : a.percentual_realizado - b.percentual_realizado
    );

    // Dividir em melhores (primeira metade) e piores (segunda metade)
    const meio = Math.ceil(ordenadas.length / 2);
    const melhores = ordenadas.slice(0, meio);
    const piores = ordenadas.slice(meio);

    return {
      top10: melhores,
      bottom10: piores,
      allData: todasEquipes // Lista única sem duplicatas para estatísticas
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingData, sortOrder, equipesPermitidas]);

  // Dados combinados ordenados para estatísticas gerais
  const sortedData = allData;

  // Processar dados de contratos para o gráfico
  const chartDataContratos = React.useMemo(() => {
    if (!contratosData || !Array.isArray(contratosData)) return [];
    
    const sortedContratos = [...contratosData].sort((a, b) => {
      const aVal = typeof a.percentual_realizado === 'number' ? a.percentual_realizado : -99999;
      const bVal = typeof b.percentual_realizado === 'number' ? b.percentual_realizado : -99999;
      return bVal - aVal;
    });

    return sortedContratos.map(item => ({
      des_equipe_cms: item.des_contrato || item.contrato || 'N/A',
      total_previsto: item.total_previsto || 0,
      total_aberto: item.total_aberto || 0,
      total_realizado: item.total_aberto || 0,
      percentual_realizado: item.percentual_realizado || 0
    }));
  }, [contratosData]);

  const renderTable = (data, title, icon, isBottom = false) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: headerBg }}>
              <TableCell sx={{ fontWeight: 700, width: 80 }}>Posição</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Equipe</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} align="center">Turnos Previstos</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} align="center">Total Realizados</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  % Médio Realizado
                  <Tooltip title={sortOrder === 'desc' ? 'Ordenar do menor para o maior' : 'Ordenar do maior para o menor'}>
                    <IconButton 
                      size="small" 
                      onClick={handleToggleSortOrder}
                      sx={{ 
                        padding: 0.5,
                        '&:hover': { 
                          backgroundColor: 'action.hover',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      {sortOrder === 'desc' ? (
                        <ArrowDownwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      ) : (
                        <ArrowUpwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" variant="body2">
                    Nenhum dado encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => {
                const position = isBottom 
                  ? sortedData.length - (data.length - idx) + 1
                  : idx + 1;
                
                return (
                  <TableRow 
                    key={row.des_equipe_cms || idx}
                    sx={{ 
                      backgroundColor: idx % 2 === 0 ? rowBgEven : rowBgOdd,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {getRankIcon(position)}
                    </TableCell>
                    <TableCell>{row.des_equipe_cms || 'N/A'}</TableCell>
                    <TableCell align="center">{Math.round(row.total_previsto) || '--'}</TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {Math.round(row.total_realizado) || '--'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${row.percentual_realizado.toFixed(2)}%`}
                        color={getRealizadoColor(row.percentual_realizado)}
                        size="small"
                        sx={{ fontWeight: 600, minWidth: 70 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEventsIcon color="primary" />
          Ranking de Equipes - Percentual Médio
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Clique no ícone ao lado de "% Médio Realizado" para alternar a ordenação
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} minHeight={300}>
            <Typography color="error">
              {error || 'Erro ao carregar dados'}
            </Typography>
            <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </Box>
        ) : chartDataContratos.length === 0 && sortedData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <Typography color="text.secondary">
              Nenhum dado encontrado para o período selecionado.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Gráfico de Ranking de Barras Horizontais - Por Contrato */}
            {chartDataContratos.length > 0 && (
              <RankingBarChart
                data={chartDataContratos}
                title="Ranking de Turnos por Contrato"
                height={Math.max(300, chartDataContratos.length * 45)}
                loading={loading}
              />
            )}

            <Divider sx={{ my: 3 }} />

            {/* Melhores Equipes */}
            {renderTable(
              top10,
              `Melhores Equipes (${top10.length}) 🏆`,
              <EmojiEventsIcon color="success" fontSize="large" />
            )}

            <Divider sx={{ my: 3 }} />

            {/* Equipes que Precisam de Atenção */}
            {renderTable(
              bottom10,
              `Equipes que Precisam de Atenção (${bottom10.length}) ⚠️`,
              <TrendingDownIcon color="error" fontSize="large" />,
              true
            )}

            {/* Estatísticas Gerais */}
            <Box sx={{ mt: 3, p: 2, bgcolor: boxBg, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                📊 Estatísticas Gerais
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total de Equipes:</strong> {sortedData.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Melhor Equipe:</strong> {' '}
                  {top10[0]?.des_equipe_cms || 'N/A'} ({top10[0]?.percentual_realizado?.toFixed(2) || '0'}% médio)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Pior Equipe:</strong> {' '}
                  {bottom10[0]?.des_equipe_cms || 'N/A'} ({bottom10[0]?.percentual_realizado?.toFixed(2) || '0'}% médio)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Percentual Médio Geral:</strong> {' '}
                  {sortedData.length > 0 
                    ? (sortedData.reduce((sum, item) => sum + item.percentual_realizado, 0) / sortedData.length).toFixed(2)
                    : '0'
                  }%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Geral Realizado:</strong> {' '}
                  {Math.round(sortedData.reduce((sum, item) => sum + item.total_realizado, 0))} turnos
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TurnosRankingModal;

